import {FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {ChangeDetectionStrategy, Component, computed, Inject, Injectable, signal} from "@angular/core";
import {lyttTilEsc} from "felles-ng2/støttefunksjoner/tastaturhendelser";
import {MeldingTilBruker} from "felles/tjenester/MeldingTilBruker";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {EnkelGruppeModell, GruppeMedlemListeModell, GruppeMedlemRessursClient, MedlemAnonymiserSpesifikasjon, MedlemAssosiasjonerModell, MedlemId} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {GruppemedlemDatatjeneste} from "felles-ng2/tjenester/datalager/GruppemedlemDatatjeneste";
import {TranslateModule} from "@ngx-translate/core";
import {MatButtonModule} from "@angular/material/button";
import {MatError, MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {tomMedlemAssosiasjonerModell} from "felles-ng2/modell/medlemModellHjelpere";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";
import {litenIkkeTomStreng} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {StandardValideringsfeilPipe} from "felles-ng2/pipes/StandardValideringsfeilPipe";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {lagrerSignal} from "felles-ng2/tjenester/LagrerIndikator";

const debug = initDebugger('MedlemAnonymiserDialogKomponent');

export type MedlemAnonymiserDialogRespons = boolean;


@Component({
    selector: 'medlem-anonymiser-dialog',
    templateUrl: 'medlem-anonymiser-dialog.html',
    styleUrl: 'medlem-anonymiser-dialog.scss',
    host: { 'class': 'e2e-anonymisermedlemdialog' },
    imports: [
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatError,
        MatFormField,
        MatInput,
        MatLabel,
        TranslateModule,
        InlineMeldingKomponent,
        StandardValideringsfeilPipe,
        VisSpinnerDirektiv,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MedlemAnonymiserDialogKomponent {

    static vis(dialog: MatDialog, medlem: GruppeMedlemListeModell, gruppe: EnkelGruppeModell): MatDialogRef<MedlemAnonymiserDialogKomponent, MedlemAnonymiserDialogRespons> {
        return dialog.open(MedlemAnonymiserDialogKomponent, {data: {medlem, gruppe} as DialogParametre, disableClose: true, ariaLabelledBy: 'medlem-anonymiser-dialog-tittel', maxWidth: '600px'});
    }

    medlem!: GruppeMedlemListeModell;
    gruppe!: EnkelGruppeModell;

    skjema = new FormGroup({
        aarsak: new FormControl('', {nonNullable: true, validators: litenIkkeTomStreng})
    });
    assosiasjoner = signal<MedlemAssosiasjonerModell>(tomMedlemAssosiasjonerModell());
    harSlettbareMoeteAssosiasjoner = computed(() =>
      this.assosiasjoner().antTilknyttetFremtidigIkkelaastMoeteSomDeltaker > 0 ||
      this.assosiasjoner().antTilknyttetFremtidigIkkelaastProtokollSomDeltaker > 0 ||
      this.assosiasjoner().antTilknyttetFremtidigIkkelaastSakSomAnsvarlig > 0);
    lagrer = lagrerSignal();

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private avhengigheter: MedlemAnonymiserDialogKomponentAvhengigheter,
                private meldingTilBruker: MeldingTilBruker,
                private oversettelser: Oversettelser,
                public dialogRef: MatDialogRef<MedlemAnonymiserDialogKomponent, MedlemAnonymiserDialogRespons>) {
            //dialog er konf med disableClose:true for å unngå at klikk utenfor dialog lukker denne, ønsker fortsatt at esc skal lukke
            lyttTilEsc(() => this.avbryt());

        this.medlem = parametre.medlem;
        this.gruppe = parametre.gruppe;

        this.avhengigheter.assosiasjoner(this.medlem.id)
          .then(assosiasjoner => this.assosiasjoner.set(assosiasjoner));
    }

    avbryt() {
        this.dialogRef.close(false);
    }

    async anonymiser() {
        await this.lagrer.utfør(async () => {
            await this.avhengigheter.anonymiser(this.medlem.id, this.somSpesifikasjon());
            this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n('Tilgang til gruppe stengt permanent for {navnEllerEpost}'), {navnEllerEpost: this.medlem.navn || this.medlem.epost}));
            this.dialogRef.close(true);
        });
    }

    //hjelpemetoder

    private somSpesifikasjon(): MedlemAnonymiserSpesifikasjon {
        return {
            aarsak: this.skjema.value.aarsak!
        };
    }
}

export interface DialogParametre {
    medlem: GruppeMedlemListeModell;
    gruppe: EnkelGruppeModell;
}

@Injectable()
export class MedlemAnonymiserDialogKomponentAvhengigheter {

    constructor(private gruppeMedlemRessurs: GruppeMedlemRessursClient, private gruppemedlemDatatjeneste: GruppemedlemDatatjeneste) {
    }

    assosiasjoner(id: MedlemId): Promise<MedlemAssosiasjonerModell> {
        return this.gruppeMedlemRessurs.assosiasjoner(id).somPromise();
    }

    async anonymiser(id: MedlemId, spesifikasjon: MedlemAnonymiserSpesifikasjon): Promise<void> {
        await this.gruppemedlemDatatjeneste.anonymiser(id, spesifikasjon);
    }
}
